import React, { useState, useEffect, useCallback } from 'react';
import { Job, FilterState } from '../types';
import { listJobs, searchJobs } from '../services/api';
import { getRetentionSettings } from '../services/systemApi';
import { format } from 'date-fns';
import FilterPanel from './FilterPanel';
import SearchBar from './SearchBar';
import MetricsPanel from './MetricsPanel';
import { useAutoRefresh } from '../hooks/useAutoRefresh';
import { TimelineModal } from './TimelineModal';
import { SystemErrorBanner } from './SystemErrorBanner';

/**
 * JobDashboard Component
 * 
 * Integrates JobListView with FilterPanel and SearchBar.
 * Handles filtering, searching, and pagination with auto-refresh.
 * Provides Press/Digital tab switching for separate job views.
 * 
 * Requirements: 2.6, 3.4, 9.1, 9.2, 9.3, 9.5
 */

type DashboardTab = 'press' | 'digital';

interface JobDashboardProps {
  autoRefresh?: boolean;
  refreshInterval?: number; // milliseconds
}

export const JobDashboard: React.FC<JobDashboardProps> = ({
  autoRefresh = true,
  refreshInterval = 30000, // 30 seconds
}) => {
  const [jobs, setJobs] = useState<Job[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [totalCount, setTotalCount] = useState<number>(0);
  
  // Filter and search state
  const [filters, setFilters] = useState<FilterState>({});
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [isSearchMode, setIsSearchMode] = useState<boolean>(false);
  const [selectedJobId, setSelectedJobId] = useState<string | null>(null);

  // Dashboard tab state - Requirement 9.1
  const [dashboardTab, setDashboardTab] = useState<DashboardTab>('press');

  // Retention settings - read DynamoDB TTL from config
  const [retentionDays, setRetentionDays] = useState<number>(90);

  useEffect(() => {
    getRetentionSettings()
      .then((settings) => {
        if (settings.dynamoDbTtlDays) {
          setRetentionDays(settings.dynamoDbTtlDays);
        }
      })
      .catch(() => {
        // Silently fall back to default
      });
  }, []);

  const itemsPerPage = 50;

  /**
   * Map date range preset to metrics API period
   */
  const getMetricsPeriod = (): '1h' | '24h' | '7d' | '30d' => {
    const preset = filters.dateRangePreset;
    switch (preset) {
      case 'last1h': return '1h';
      case 'last24h': return '24h';
      case 'last7d': return '7d';
      case 'last30d': return '30d';
      default: return '1h'; // default matches FilterPanel's default preset
    }
  };

  /**
   * Fetch jobs from API with filters
   * Requirement 2.6: Apply multiple filters with AND logic
   */
  const fetchJobs = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      let response;
      
      if (isSearchMode && searchQuery) {
        // Search mode - Requirement 3.4: Apply search query
        const searchResult = await searchJobs(searchQuery);
        
        // Sort by startTime descending
        const sortedJobs = [...searchResult.jobs].sort((a, b) => b.startTime - a.startTime);
        
        // Apply client-side pagination for search results
        const startIndex = (currentPage - 1) * itemsPerPage;
        const endIndex = startIndex + itemsPerPage;
        const paginatedJobs = sortedJobs.slice(startIndex, endIndex);
        
        setJobs(paginatedJobs);
        setTotalCount(searchResult.totalCount);
        setTotalPages(Math.ceil(searchResult.totalCount / itemsPerPage));
      } else {
        // Build effective filters based on active tab
        // Requirement 9.2: Digital tab shows press="digital", Press tab excludes digital
        const effectiveFilters: FilterState = { ...filters };
        if (dashboardTab === 'digital') {
          effectiveFilters.press = 'digital';
        } else if (!effectiveFilters.press) {
          // Press tab with no specific press filter: exclude digital jobs server-side
          effectiveFilters.excludePress = 'digital';
        }

        // Filter mode - Requirement 2.6: Apply filters to API query
        response = await listJobs(effectiveFilters, currentPage, itemsPerPage);
        
        // Sort by startTime descending
        let sortedJobs = [...response.jobs].sort((a, b) => b.startTime - a.startTime);
        
        setJobs(sortedJobs);
        setTotalCount(response.totalCount);
        setTotalPages(response.totalPages);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load jobs');
      console.error('Error fetching jobs:', err);
    } finally {
      setLoading(false);
    }
  }, [filters, currentPage, isSearchMode, searchQuery, dashboardTab]);

  /**
   * Auto-refresh hook
   * Requirement 6.1: Refresh every 30 seconds
   * Requirement 6.2: Preserve filter and search settings during refresh
   * Requirement 6.5: Display last refresh timestamp
   */
  const { lastRefresh, isRefreshing, refresh: manualRefresh } = useAutoRefresh({
    interval: refreshInterval,
    enabled: autoRefresh,
    onRefresh: fetchJobs,
  });

  /**
   * Fetch jobs when filters, search, or page changes
   */
  useEffect(() => {
    fetchJobs();
  }, [fetchJobs]);

  /**
   * Handle filter change
   */
  const handleFilterChange = (newFilters: FilterState) => {
    setFilters(newFilters);
    setCurrentPage(1); // Reset to first page when filters change
    setIsSearchMode(false); // Exit search mode when filters change
    setSearchQuery('');
  };

  /**
   * Handle dashboard tab change
   * Requirement 9.1: Switch between Press and Digital views
   */
  const handleTabChange = (tab: DashboardTab) => {
    setDashboardTab(tab);
    setFilters({});
    setCurrentPage(1);
    setIsSearchMode(false);
    setSearchQuery('');
  };

  /**
   * Handle search
   */
  const handleSearch = (query: string) => {
    if (query) {
      setSearchQuery(query);
      setIsSearchMode(true);
      setCurrentPage(1); // Reset to first page when searching
      setFilters({}); // Clear filters when searching
    } else {
      setSearchQuery('');
      setIsSearchMode(false);
    }
  };

  /**
   * Handle page change
   */
  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  /**
   * Handle manual refresh
   */
  const handleManualRefresh = () => {
    manualRefresh();
  };

  /**
   * Format timestamp to local timezone
   */
  const formatTimestamp = (timestamp?: number): string => {
    if (!timestamp) return '-';
    return format(new Date(timestamp), 'yyyy-MM-dd HH:mm:ss');
  };

  /**
   * Get row styling for failed jobs
   */
  const getRowClassName = (status: string): string => {
    const baseClasses = 'border-b hover:bg-gray-50';
    if (status === 'failed') {
      return `${baseClasses} bg-red-50 text-red-900`;
    }
    return baseClasses;
  };

  return (
    <div className="w-full">
      {/* System Error Banner - shows infrastructure/environment errors above tabs */}
      <SystemErrorBanner />

      {/* Press / Digital Tab Switcher - Requirement 9.1 */}
      <div className="flex border-b border-gray-200 mb-4 mx-4">
        <button
          onClick={() => handleTabChange('press')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            dashboardTab === 'press'
              ? 'border-blue-600 text-blue-600'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
          }`}
        >
          Press
        </button>
        <button
          onClick={() => handleTabChange('digital')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            dashboardTab === 'digital'
              ? 'border-blue-600 text-blue-600'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
          }`}
        >
          Digital
        </button>
      </div>

      {/* Data Retention Notice - Requirement 9.1 */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4 mx-4">
        <div className="flex items-start">
          <svg className="w-5 h-5 text-blue-600 mt-0.5 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
          </svg>
          <div>
            <p className="text-sm text-blue-800">
              <span className="font-semibold">Data Retention:</span> Job records are retained for {retentionDays} days. Older records are automatically deleted.
            </p>
          </div>
        </div>
      </div>

      {/* Metrics Panel - synced with time filter */}
      <div className="mx-4 mb-4">
        <MetricsPanel period={getMetricsPeriod()} />
      </div>

      {/* Filter Panel - Requirement 9.5: hide press filter on Digital tab */}
      {!isSearchMode && (
        <FilterPanel
          filters={filters}
          onChange={handleFilterChange}
          hidePressFilter={dashboardTab === 'digital'}
        />
      )}

      {/* Search Bar */}
      <SearchBar
        onSearch={handleSearch}
        resultsCount={isSearchMode ? totalCount : undefined}
      />

      {/* Header with last refresh timestamp and manual refresh button */}
      <div className="flex justify-between items-center mb-4 px-4">
        <div className="text-sm text-gray-600">
          {isSearchMode ? (
            <span>Search results: {totalCount} jobs</span>
          ) : (
            <span>Showing {jobs.length} of {totalCount} jobs</span>
          )}
        </div>
        <div className="flex items-center gap-4">
          <div className="text-sm text-gray-600">
            Last refresh: {lastRefresh ? format(lastRefresh, 'yyyy-MM-dd HH:mm:ss') : 'Never'}
          </div>
          <button
            onClick={handleManualRefresh}
            disabled={isRefreshing}
            className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
          >
            {isRefreshing ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>
      </div>

      {/* Loading state */}
      {loading && jobs.length === 0 && (
        <div className="flex justify-center items-center h-64">
          <div className="text-gray-500">Loading jobs...</div>
        </div>
      )}

      {/* Error state */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded p-4 m-4">
          <h3 className="text-red-800 font-semibold mb-2">Error Loading Jobs</h3>
          <p className="text-red-700">{error}</p>
          <button
            onClick={() => fetchJobs()}
            className="mt-4 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
          >
            Retry
          </button>
        </div>
      )}

      {/* Empty state - Requirement 3.4: Display "no results" message */}
      {!loading && !error && jobs.length === 0 && (
        <div className="flex justify-center items-center h-64">
          <div className="text-gray-500">
            {isSearchMode ? 'No jobs found matching your search' : 'No jobs found'}
          </div>
        </div>
      )}

      {/* Job table */}
      {!loading && !error && jobs.length > 0 && (
        <>
          <div className="overflow-x-auto">
            <table className="min-w-full bg-white border border-gray-200">
              <thead className="bg-gray-100">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider border-b">
                    Job ID
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider border-b">
                    Filename
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider border-b">
                    Site
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider border-b">
                    Press
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider border-b">
                    Status
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider border-b">
                    Start Time
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider border-b">
                    End Time
                  </th>
                </tr>
              </thead>
              <tbody>
                {jobs.map((job) => (
                  <tr
                    key={job.jobId}
                    className={`${getRowClassName(job.status)} cursor-pointer`}
                    onClick={() => setSelectedJobId(job.jobId)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setSelectedJobId(job.jobId);
                      }
                    }}
                    role="button"
                    tabIndex={0}
                  >
                    <td className="px-4 py-3 text-sm font-mono truncate max-w-xs" title={job.jobId}>
                      {job.jobId}
                    </td>
                    <td className="px-4 py-3 text-sm truncate max-w-xs" title={job.filename}>
                      {job.filename}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {job.site}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {job.press}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        job.status === 'completed' ? 'bg-green-100 text-green-800' :
                        job.status === 'failed' ? 'bg-red-100 text-red-800' :
                        job.status === 'processing' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {job.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {formatTimestamp(job.startTime)}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {formatTimestamp(job.endTime)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination controls */}
          {totalPages > 1 && (
            <div className="flex justify-between items-center mt-4 px-4">
              <div className="text-sm text-gray-600">
                Page {currentPage} of {totalPages}
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => handlePageChange(1)}
                  disabled={currentPage === 1}
                  className="px-3 py-1 border rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
                >
                  First
                </button>
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="px-3 py-1 border rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
                >
                  Previous
                </button>
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="px-3 py-1 border rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
                >
                  Next
                </button>
                <button
                  onClick={() => handlePageChange(totalPages)}
                  disabled={currentPage === totalPages}
                  className="px-3 py-1 border rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
                >
                  Last
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Loading indicator during refresh */}
      {isRefreshing && (
        <div className="fixed bottom-4 right-4 bg-blue-500 text-white px-4 py-2 rounded shadow-lg">
          Refreshing...
        </div>
      )}

      {/* Timeline Modal */}
      {selectedJobId && (
        <TimelineModal jobId={selectedJobId} onClose={() => setSelectedJobId(null)} />
      )}
    </div>
  );
};

export default JobDashboard;
